import { Action } from 'redux';
import {
    Journey,
    JourneyDailySummary,
    JourneyStatus,
    JourneyStepSummary,
    JourneySummary,
    Template,
} from '@dev/ee-api/ee-api-models';

import {
    GetAutomationContactsParams,
    GetAutomationDailySummaryParams,
    GetAutomationsParams,
    ListFromAutomationParams,
} from '@/services/ElasticApi';

export interface AutomationsInterface {
    collection?: JourneySummary[];
    loading?: boolean;
    statusCounts?: AutomationsStatusCounts;
    pagination?: AutomationsPagination;
    automation?: Journey;
    loadingStatusUpdate?: boolean;
    checkedAutomations?: string[];
    singleAutomation?: JourneySummary;
    emailsList?: AutomationStepSummaryAndTemplate[];
    loadingSingleAutomation?: boolean;
    singleAutomationDailySummary?: JourneyDailySummary[];
    dailySummaryPagination?: DailySummaryPagination;
    loadingDailySummary?: boolean;
    contactsList?: string[];
    contactsTotalCount?: number;
    contactsListPagination?: ContactsListPagination;
    loadingContactsList?: boolean;
    previewTemplate: PreviewTemplate;
    listFromAutomationLoading: boolean;
}

export interface AutomationStepSummaryAndTemplate extends JourneyStepSummary {
    TemplateData: Template;
    TemplateName: string;
}
export interface UrlSearchParams {
    page?: number;
    perPage?: number;
    search?: string;
    from?: string;
    to?: string;
    status?: [];
}

export interface AutomationsStatusCounts {
    total: number;
    active: number;
    draft: number;
    paused: number;
    invalid: number;
}

export interface AutomationsPagination {
    page?: number;
    perPage?: number;
    from?: string;
    to?: string;
    name?: string;
    statuses?: JourneyStatus[];
    search?: string;
}

export interface ContactsListPagination {
    page?: number;
    perPage?: number;
    from?: string;
    to?: string;
}

export interface DailySummaryPagination {
    from?: string;
    to?: string;
    channelName?: string;
}

export enum AutomationsActionTypes {
    GET_ALL_AUTOMATIONS_START = '@@automations/GET_ALL_AUTOMATIONS_START',
    GET_ALL_AUTOMATIONS_SUCCESS = '@@automations/GET_ALL_AUTOMATIONS_SUCCESS',
    GET_ALL_AUTOMATIONS_ERROR = '@@automations/GET_ALL_AUTOMATIONS_ERROR',
    GET_AUTOMATION_BY_NAME_START = '@@automations/GET_AUTOMATION_BY_NAME_START',
    GET_AUTOMATION_BY_NAME_SUCCESS = '@@automations/GET__AUTOMATION_BY_NAME_SUCCESS',
    GET_AUTOMATION_BY_NAME_ERROR = '@@automations/GET_AUTOMATION_BY_NAME_ERROR',
    GET_AUTOMATION_DAILY_SUMMARY_START = '@@automations/GET_AUTOMATION_DAILY_SUMMARY_START',
    GET_AUTOMATION_DAILY_SUMMARY_SUCCESS = '@@automations/GET_AUTOMATION_DAILY_SUMMARY_SUCCESS',
    GET_AUTOMATION_DAILY_SUMMARY_ERROR = '@@automations/GET_AUTOMATION_DAILY_SUMMARY_ERROR',
    GET_AUTOMATION_CONTACTS_START = '@@automations/GET_AUTOMATION_CONTACTS_START',
    GET_AUTOMATION_CONTACTS_SUCCESS = '@@automations/GET_AUTOMATION_CONTACTS_SUCCESS',
    GET_AUTOMATION_CONTACTS_ERROR = '@@automations/GET_AUTOMATION_CONTACTS_ERROR',
    UPDATE_AUTOMATION_STATUS_SUCCESS = '@@automations/UPDATE_AUTOMATION_STATUS_SUCCESS',
    UPDATE_AUTOMATION_STATUS_ERROR = '@@automations/UPDATE_AUTOMATION_STATUS_ERROR',
    UPDATE_AUTOMATION_STATUS = '@@automations/UPDATE_AUTOMATION_STATUS',
    UPDATE_AUTOMATION_STATUS_CONFIRMATION = '@@automations/UPDATE_AUTOMATION_STATUS_CONFIRMATION',
    REACTIVATE_AUTOMATION = '@@automations/REACTIVATE_AUTOMATION',
    DELETE_AUTOMATIONS = '@@automations/DELETE_AUTOMATIONS',
    PICK_AUTOMATIONS = '@@automations/PICK_AUTOMATIONS',
    UNPICK_AUTOMATIONS = '@@automations/UNPICK_AUTOMATIONS',
    LOAD_PREVIEW_TEMPLATE = '@@automations/LOAD_PREVIEW_TEMPLATE',
    LOAD_PREVIEW_TEMPLATE_SUCCESS = '@@automations/LOAD_PREVIEW_TEMPLATE_SUCCESS',
    LOAD_PREVIEW_TEMPLATE_ERROR = '@@automations/LOAD_PREVIEW_TEMPLATE_ERROR',
    CLEAR_PREVIEW_TEMPLATE = '@@automations/CLEAR_PREVIEW_TEMPLATE',
    CREATE_LIST_FROM_AUTOMATION = '@@automations/CREATE_LIST_FROM_AUTOMATION',
    CREATE_LIST_FROM_AUTOMATION_SUCCESS = '@@automations/CREATE_LIST_FROM_AUTOMATION_SUCCESS',
    CREATE_LIST_FROM_AUTOMATION_ERROR = '@@automations/CREATE_LIST_FROM_AUTOMATION_ERROR',
}

export interface GetAllAutomationsStartAction extends Action {
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_START;
    payload: GetAutomationsParams;
}

export interface GetAllAutomationsSuccessAction extends Action {
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_SUCCESS;
    payload: {
        collection: JourneySummary[];
        statusCounts: AutomationsStatusCounts;
        pagination: AutomationsPagination;
    };
}

export interface GetAllAutomationsErrorAction extends Action {
    type: AutomationsActionTypes.GET_ALL_AUTOMATIONS_ERROR;
}

export interface UpdateAutomationStatusParams {
    name: string;
    status: JourneyStatus;
    isEditMode?: boolean;
}
export interface DeleteAutomationsPayload {
    Names?: Array<string>;
}
export interface UpdateAutomationStatusSuccessAction extends Action {
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_SUCCESS;
    payload: Journey;
}

export interface UpdateAutomationStatusErrorAction extends Action {
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_ERROR;
}

export interface UpdateAutomationStatusAction extends Action {
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS;
    payload: UpdateAutomationStatusParams;
}
export interface UpdateAutomationStatusConfirmationAction extends Action {
    type: AutomationsActionTypes.UPDATE_AUTOMATION_STATUS_CONFIRMATION;
    payload: UpdateAutomationStatusParams;
}

export interface ReactivateAutomationAction extends Action {
    type: AutomationsActionTypes.REACTIVATE_AUTOMATION;
    payload: UpdateAutomationStatusParams;
}

export interface DeleteAutomationAction extends Action {
    type: AutomationsActionTypes.DELETE_AUTOMATIONS;
    payload: DeleteAutomationsPayload;
}
export interface PickAutomationsAction extends Action {
    type: AutomationsActionTypes.PICK_AUTOMATIONS;
    payload: {
        checkedAutomations: string[];
    };
}
export interface UnpickAllAutomationsAction extends Action {
    type: AutomationsActionTypes.UNPICK_AUTOMATIONS;
}

export interface GetAutomationByNameStartAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_START;
    payload: { name: string; isTemplateThumbnailsRequired?: boolean };
}

export interface GetAutomationByNameSuccessAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_SUCCESS;
    payload: {
        singleAutomation: JourneySummary;
        emailsList: AutomationStepSummaryAndTemplate[];
    };
}

export interface GetAutomationByNameErrorAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_BY_NAME_ERROR;
}

export interface GetAutomationDailySummaryStartAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_START;
    payload: GetAutomationDailySummaryParams;
}

export interface GetAutomationDailySummarySuccessAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_SUCCESS;
    payload: {
        singleAutomationDailySummary: JourneyDailySummary[];
        dailySummaryPagination: DailySummaryPagination;
    };
}

export interface GetAutomationDailySummaryErrorAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_DAILY_SUMMARY_ERROR;
}

export interface GetAutomationContactsStartAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_START;
    payload: GetAutomationContactsParams;
}

export interface GetAutomationContactsSuccessAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_SUCCESS;
    payload: {
        contactsList: string[];
        contactsListPagination: ContactsListPagination;
        contactsTotalCount: number;
    };
}

export interface GetAutomationContactsErrorAction extends Action {
    type: AutomationsActionTypes.GET_AUTOMATION_CONTACTS_ERROR;
}

export interface LoadPreviewTemplateAction extends Action {
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE;
    payload: {
        name: string;
    };
}

export interface PreviewTemplate {
    html: string;
    name: string;
}
export interface LoadPreviewTemplateSuccessAction extends Action {
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE_SUCCESS;
    payload: PreviewTemplate;
}

export interface LoadPreviewTemplateErrorAction extends Action {
    type: AutomationsActionTypes.LOAD_PREVIEW_TEMPLATE_ERROR;
}

export interface ClearPreviewTemplateAction extends Action {
    type: AutomationsActionTypes.CLEAR_PREVIEW_TEMPLATE;
    payload: null;
}

export interface ListFromAutomationAction extends Action {
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION;
    payload: {
        params: ListFromAutomationParams;
    };
}

export interface ListFromAutomationSuccessAction extends Action {
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION_SUCCESS;
}

export interface ListFromAutomationErrorAction extends Action {
    type: AutomationsActionTypes.CREATE_LIST_FROM_AUTOMATION_ERROR;
}

export enum DropdownOptions {
    ViewWorkflow = 'viewworkflow',
    Pause = 'pause',
    Edit = 'edit',
    PauseAndEdit = 'pauseandedit',
    Reactivate = 'reactivate',
    Delete = 'delete',
    CopyCampaign = 'CopyCampaign',
}
